import React, { Component } from "react";
import { Widget, addResponseMessage } from "react-chat-widget";
import "react-chat-widget/lib/styles.css";
import Editor from "@monaco-editor/react";
import socketClient from "socket.io-client";
const { REACT_APP_BACKEND } = process.env;
const SERVER = REACT_APP_BACKEND;

export default class EditorWrapper extends Component {
  users = {};
  decorations = {};
  socket = null;
  editorRef = null;
  _editor = undefined;
  contentWidgets = {};
  collabrators = [];
  _applyingRemote = false;
  diagnosticsDecorations = [];
  followTarget = null;
  isScrolling = false;
  fileTree = {};
  constructor(props) {
    super(props);
    this.state = {
      monaco: null,
      language: props.language,
      code: props.code,
      meetingCode: props.meetingCode,
      isFirstTime: true,
      clientId: null,
      followMode: false,
      followTargetName: null,
      files: {},
      activeFile: "main.js",
      showFileTree: true,
      diagnostics: [],
    };
    if(this.props.socket){
      this.socket = this.props.socket;
    }
    this.decorations = this.props.decorations;
    this.handleNewUserMessage.bind(this);
  }

  handleEditorDidMount = (editor, monaco) => {
    this._editor = editor;
    this._monaco = monaco;

    this._markerSubscription = monaco.editor.onDidChangeMarkers((uris) => {
      const model = editor.getModel();
      if (model && uris.includes(model.uri.toString())) {
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        this.handleDiagnostics(model.uri, markers);
      }
    });

    if (!monaco.languages.getLanguages().find((l) => l.id === "zig")) {
      monaco.languages.register({ id: "zig" });
      monaco.languages.setMonarchTokensProvider("zig", {
        keywords: [
          "const", "var", "fn", "pub", "if", "else", "while", "for",
          "return", "break", "continue", "defer", "errdefer", "try",
          "catch", "switch", "match", "struct", "enum", "union",
          "opaque", "extern", "export", "volatile", "threadlocal",
          "comptime", "test", "undefined", "true", "false", "null",
        ],
        typeKeywords: [
          "bool", "f16", "f32", "f64", "f128",
          "i8", "i16", "i32", "i64", "i128",
          "u8", "u16", "u32", "u64", "u128",
          "usize", "isize", "c_short", "c_ushort", "c_int",
          "c_uint", "c_long", "c_ulong", "c_longlong", "c_ulonglong",
          "c_longdouble", "anyerror", "noreturn", "void", "type",
          "anytype", "anyframe", "anyopaque", "usize",
        ],
        operators: [
          "=", ">", "<", "!", "~", "?", ":",
          "==", "<=", ">=", "!=", "&&", "||", "++", "--",
          "+", "-", "*", "/", "%", "&", "|", "^", "<<", ">>",
          "+=", "-=", "*=", "/=", "%=", "&=", "|=", "^=", "<<=", ">>=",
          "..", "...",
        ],
        symbols: /[=><!~?:&|+\-*/^%]+/,
        tokenizer: {
          root: [
            [/[a-zA-Z_]\w*/, {
              cases: {
                "@keywords": "keyword",
                "@typeKeywords": "type",
                "@default": "identifier",
              },
            }],
            { include: "@whitespace" },
            [/[{}()[\]]/, "delimiter"],
            [/@symbols/, {
              cases: {
                "@operators": "operator",
                "@default": "",
              },
            }],
            [/\d*\.\d+([eE][-+]?\d+)?/, "number.float"],
            [/0[xX][0-9a-fA-F]+/, "number.hex"],
            [/\d+/, "number"],
            [/"([^"\\]|\\.)*$/, "string.invalid"],
            [/"/, { token: "string.quote", bracket: "@open", next: "@string" }],
            [/\/\/.*$/, "comment"],
            [/\/\*/, "comment", "@comment"],
          ],
          whitespace: [
            [/[ \t\r\n]+/, "white"],
          ],
          comment: [
            [/[^/*]+/, "comment"],
            [/\/\*/, "comment", "@push"],
            ["\\*/", "comment", "@pop"],
            [/[/*]/, "comment"],
          ],
          string: [
            [/[^\\"]+/, "string"],
            [/\\./, "string.escape"],
            [/"/, { token: "string.quote", bracket: "@close", next: "@pop" }],
          ],
        },
      });
    }

    editor.onDidChangeCursorSelection((e) => {
      this.socket.emit("selection", {
        event: e,
        meetingCode: this.props.meetingCode,
        userId: this.state.clientId,
        fileName: this.state.activeFile,
      });
    });

    editor.onDidChangeModelContent((e) => {
      const val = editor.getValue();
      this.showValue(val, e);
    });

    this._scrollThrottle = null;
    editor.onDidScrollChange((e) => {
      if (this._scrollThrottle) return;
      this._scrollThrottle = setTimeout(() => { this._scrollThrottle = null; }, 100);
      if (!this.isScrolling) {
        const scrollTop = editor.getScrollTop();
        const scrollLeft = editor.getScrollLeft();
        this.socket.emit("scroll-position", {
          meetingCode: this.props.meetingCode,
          userId: this.state.clientId,
          scrollTop,
          scrollLeft,
        });
      }
    });
  };

  handleDiagnostics = (uri, markers) => {
    if (!this._editor || !this._monaco) return;
    const model = this._editor.getModel();
    if (!model || model.uri.toString() !== uri.toString()) return;

    const diagnostics = markers.map((m) => ({
      severity: m.severity,
      message: m.message,
      startLineNumber: m.startLineNumber,
      startColumn: m.startColumn,
      endLineNumber: m.endLineNumber,
      endColumn: m.endColumn,
    }));

    this.setState({ diagnostics });

    if (this.socket) {
      this.socket.emit("diagnostics", {
        meetingCode: this.props.meetingCode,
        fileName: this.state.activeFile,
        diagnostics,
      });
    }
  };

  applyRemoteDiagnostics(data) {
    if (!this._editor || !this._monaco) return;
    if (data.fileName !== this.state.activeFile) return;

    const newDecorations = data.diagnostics.map((d) => {
      const severityMap = {
        1: this._monaco.MarkerSeverity.Error,
        2: this._monaco.MarkerSeverity.Warning,
        4: this._monaco.MarkerSeverity.Info,
        8: this._monaco.MarkerSeverity.Hint,
      };
      return {
        range: new this._monaco.Range(
          d.startLineNumber, d.startColumn,
          d.endLineNumber, d.endColumn
        ),
        options: {
          inlineClassName: d.severity === 1 ? "remote-error-decoration" :
                         d.severity === 2 ? "remote-warning-decoration" :
                         "remote-info-decoration",
          hoverMessage: { value: `**Remote ${d.severity === 1 ? 'Error' : d.severity === 2 ? 'Warning' : 'Info'}:** ${d.message}` },
        },
      };
    });

    this.diagnosticsDecorations = this._editor.deltaDecorations(
      this.diagnosticsDecorations, newDecorations
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.isFirstTime) return;

    if (prevProps.language !== this.state.language) {
      this.setState({ language: this.props.language });
    }
  }

  handleNewUserMessage = (newMessage) => {
    const message = {
      meetingCode: this.props.meetingCode,
      message: `${this.props.user}: ${newMessage}`,
      userId: this.props.user,
    };
    this.socket.emit("message", message);
  };

  insertCSS(id, color) {
    var style = document.createElement("style");
    style.type = "text/css";
    style.innerHTML += `.${id} { background-color: ${color}40 }\n`;
    style.innerHTML += `.${id}one { background: ${color}; width: 2px !important }\n`;
    style.innerHTML += `.remote-error-decoration { background: #ff000030; border-bottom: 2px wavy red; }\n`;
    style.innerHTML += `.remote-warning-decoration { background: #ffaa0030; border-bottom: 2px wavy #ffaa00; }\n`;
    style.innerHTML += `.remote-info-decoration { background: #0066ff20; border-bottom: 1px dotted #0066ff; }\n`;
    document.getElementsByTagName("head")[0].appendChild(style);
  }

  insertWidget(e) {
    this.contentWidgets[e.userId] = {
      domNode: null,
      position: {
        lineNumber: 0,
        column: 0,
      },
      getId: function () {
        return "content." + e.userId;
      },
      getDomNode: function () {
        if (!this.domNode) {
          this.domNode = document.createElement("div");
          this.domNode.className = "remote-cursor-label";
          this.domNode.innerHTML = `<span class="remote-cursor-dot" style="background:${e.color}"></span>${e.name}`;
          this.domNode.style.background = e.color + "dd";
          this.domNode.style.color = "#000";
          this.domNode.style.opacity = 0.9;
          this.domNode.style.width = "max-content";
          this.domNode.style.padding = "2px 6px";
          this.domNode.style.borderRadius = "3px";
          this.domNode.style.fontSize = "11px";
          this.domNode.style.fontWeight = "600";
          this.domNode.style.fontFamily = "Inter, sans-serif";
          this.domNode.style.pointerEvents = "none";
          this.domNode.style.zIndex = "10";
          this.domNode.style.boxShadow = "0 1px 4px rgba(0,0,0,0.2)";
          this.domNode.style.display = "flex";
          this.domNode.style.alignItems = "center";
          this.domNode.style.gap = "4px";
        }
        return this.domNode;
      },
      getPosition: function () {
        return {
          position: this.position,
          preference: [
            window.monaco.editor.ContentWidgetPositionPreference.ABOVE,
            window.monaco.editor.ContentWidgetPositionPreference.BELOW,
          ],
        };
      },
    };
  }

  changeSeleciton(data) {
    if (data.fileName && data.fileName !== this.state.activeFile) return;
    const e = data.event;
    var selectionArray = [];
    if (
      e.selection.startColumn === e.selection.endColumn &&
      e.selection.startLineNumber === e.selection.endLineNumber
    ) {
      e.selection.endColumn++;
      selectionArray.push({
        range: e.selection,
        options: {
          className: `${data.userId}one`,
          hoverMessage: {
            value: data.userName || data.userId,
          },
        },
      });
    } else {
      selectionArray.push({
        range: e.selection,
        options: {
          className: data.userId,
          hoverMessage: {
            value: data.userName || data.userId,
          },
        },
      });
    }
    for (let sel of e.secondarySelections) {
      if (
        sel.startColumn === sel.endColumn &&
        sel.startLineNumber === sel.endLineNumber
      ) {
        sel.endColumn++;
        selectionArray.push({
          range: sel,
          options: {
            className: `${data.userId}one`,
            hoverMessage: {
              value: data.userName || data.userId,
            },
          },
        });
      } else {
        selectionArray.push({
          range: sel,
          options: {
            className: data.userId,
            hoverMessage: {
              value: data.userName || data.userId,
            },
          },
        });
      }
    }
    this.decorations[data.userId] = this._editor.deltaDecorations(
      this.decorations[data.userId],
      selectionArray
    );
  }

  changeWidgetPosition(data) {
    if (data.fileName && data.fileName !== this.state.activeFile) return;
    const e = data.event;
    if (this.contentWidgets[data.userId]) {
      this.contentWidgets[data.userId].position.lineNumber =
        e.selection.endLineNumber;
      this.contentWidgets[data.userId].position.column = e.selection.endColumn;
      this._editor.removeContentWidget(this.contentWidgets[data.userId]);
      this._editor.addContentWidget(this.contentWidgets[data.userId]);
    }
  }

  handleFollowScroll(data) {
    if (!this._editor) return;
    if (data.userId === this.state.clientId) return;
    if (this.followTarget && data.userId !== this.followTarget) return;

    this.isScrolling = true;
    this._editor.setScrollPosition({
      scrollTop: data.scrollTop,
      scrollLeft: data.scrollLeft,
    });
    setTimeout(() => { this.isScrolling = false; }, 50);
  }

  switchFile(fileName) {
    const files = this.state.files;
    if (!files[fileName]) return;

    if (this.socket && this.state.activeFile) {
      this.socket.emit("coded", {
        meetingCode: this.props.meetingCode,
        text: this._editor.getValue(),
        language: this.state.language,
        clientId: this.state.clientId,
        fileName: this.state.activeFile,
        handleStuff: false,
      });
    }

    this.decorations = {};
    this.diagnosticsDecorations = [];
    if (this._editor) {
      this._editor.setValue(files[fileName].content);
    }

    this.setState({
      activeFile: fileName,
      code: files[fileName].content,
    });

    const lang = this.getLanguageFromFileName(fileName);
    if (lang !== this.state.language) {
      this.setState({ language: lang });
      this.props.onLanguageChanged(lang);
      if (this.socket) {
        this.socket.emit("language-changed", {
          meetingCode: this.props.meetingCode,
          language: lang,
        });
      }
    }

    this.socket.emit("file-switch", {
      meetingCode: this.props.meetingCode,
      fileName,
    });
  }

  getLanguageFromFileName(fileName) {
    const ext = fileName.split(".").pop().toLowerCase();
    const extMap = {
      js: "javascript", jsx: "javascript", ts: "typescript", tsx: "typescript",
      py: "python", java: "java", cpp: "cpp", c: "c", cs: "csharp",
      go: "go", rs: "rust", rb: "ruby", php: "php", swift: "swift",
      kt: "kotlin", r: "r", sql: "sql", html: "html", css: "css",
      json: "json", xml: "xml", md: "markdown", yaml: "yaml", yml: "yaml",
      sh: "shell", bash: "shell", ps1: "powershell", zig: "zig",
    };
    return extMap[ext] || "plaintext";
  }

  createNewFile() {
    const name = prompt("File name (e.g., utils.js):");
    if (!name || !name.trim()) return;
    this.socket.emit("file-create", {
      meetingCode: this.props.meetingCode,
      fileName: name.trim(),
      language: this.state.language,
    });
  }

  deleteFile(fileName, e) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    this.socket.emit("file-delete", {
      meetingCode: this.props.meetingCode,
      fileName,
    });
  }

  componentDidMount() {
    if (!this.socket) {
      this.socket = socketClient(SERVER, {
        reconnection: true,
        reconnectionDelay: 500,
        reconnectionAttempts: 10,
      });
      this.props.onSetSocket(this.socket);
    }
    this.setState({ clientId: this.socket.id });
    this.socket.on("connect", () => {
      this.setState({ clientId: this.socket.id });
    });
    this.socket.on("connection", () => {
      this.setState({ clientId: this.socket.id });
    });
    this.socket.on("channel", (channel) => {
      if (channel.name === this.props.meetingCode) {
        this.props.onUserConnect(channel.LastUserJoined.name);
        this.setState({
          code: channel.text,
          language: channel.language,
          meetingCode: channel.meetingCode,
        });
        if (this.props.language !== channel.language) {
          this.props.onLanguageChanged(channel.language);
        }
        this.props.setStrocks(channel.strokes);
      }
      if (channel.name === this.props.meetingCode) {
        this.props.onUsersChanged(channel.users);
      }
    });

    this.socket.on("message", (message) => {
      if (
        message.meetingCode === this.props.meetingCode &&
        message.userId !== this.props.user
      ) {
        addResponseMessage(message.message);
      }
    });

    this.socket.on("coded", (message) => {
      if (
        message.meetingCode === this.props.meetingCode &&
        message.clientId !== this.state.clientId
      ) {
        if (message.fileName && message.fileName !== this.state.activeFile) {
          this.setState((prev) => ({
            files: {
              ...prev.files,
              [message.fileName]: {
                ...prev.files[message.fileName],
                content: message.text,
              },
            },
          }));
          return;
        }
        this._applyingRemote = true;
        this.setState({
          code: message.text,
          language: message.language,
          meetingCode: message.meetingCode,
        }, () => {
          this._applyingRemote = false;
        });
        this.props.setCode({ text: message.text, decorations: this.decorations });
        if (this.props.language !== message.language) {
          this.props.onLanguageChanged(message.language);
        }
      }
    });

    this.socket.on("userdata", (data) => {
      for (var i of data) {
        this.users[i.userId] = i.color;
        this.insertCSS(i.userId, i.color);
        this.insertWidget(i);
        this.decorations[i.userId] = [];
      }
    });

    this.socket.emit("channel-join", this.state.meetingCode, (ack) => {
      console.log("channel Joined", ack);
    });

    this.socket.on("selection", (message) => {
      if (
        message.meetingCode === this.props.meetingCode &&
        message.userId !== this.state.clientId
      ) {
        const user = Object.values(this.users).length > 0 ? message.userId : null;
        this.changeSeleciton(message);
        this.changeWidgetPosition(message);
      }
    });

    this.socket.on("language-changed", (data) => {
      if (data.meetingCode === this.props.meetingCode) {
        this.setState({ language: data.language });
        if (this.props.language !== data.language) {
          this.props.onLanguageChanged(data.language);
        }
      }
    });

    this.socket.on("scroll-position", (data) => {
      if (data.meetingCode === this.props.meetingCode) {
        this.handleFollowScroll(data);
      }
    });

    this.socket.on("file-list", (data) => {
      if (data.meetingCode === this.props.meetingCode) {
        this.setState({ files: data.files, activeFile: data.activeFile });
        if (data.files[data.activeFile] && data.activeFile !== this.state.activeFile) {
          this.switchFile(data.activeFile);
        }
      }
    });

    this.socket.on("file-switch", (data) => {
      if (data.meetingCode === this.props.meetingCode) {
        if (data.fileName !== this.state.activeFile && this.state.files[data.fileName]) {
          this.switchFile(data.fileName);
        }
      }
    });

    this.socket.on("diagnostics", (data) => {
      if (data.meetingCode === this.props.meetingCode) {
        this.applyRemoteDiagnostics(data);
      }
    });
  }

  showValue = (value, event) => {
    if (this._applyingRemote) return;
    const text = value;
    this.props.setCode({ text, decorations: this.decorations });
    this.setState({ code: text }, () => {
      const message = {
        meetingCode: this.props.meetingCode,
        text,
        language: this.state.language,
        clientId: this.state.clientId,
        event,
        fileName: this.state.activeFile,
      };
      this.socket.emit("coded", message);
    });
  };

  toggleFollowMode(targetUserId) {
    if (this.state.followMode && this.followTarget === targetUserId) {
      this.setState({ followMode: false, followTargetName: null });
      this.followTarget = null;
    } else {
      const targetUser = this.props.loggedinUsers.find(u => u.userId === targetUserId);
      this.setState({
        followMode: true,
        followTargetName: targetUser ? targetUser.name : "Unknown",
      });
      this.followTarget = targetUserId;
    }
  }

  getFileIcon(fileName) {
    const ext = fileName.split(".").pop().toLowerCase();
    const iconMap = {
      js: "bi-filetype-js", jsx: "bi-filetype-jsx", ts: "bi-filetype-tsx",
      tsx: "bi-filetype-tsx", py: "bi-filetype-py", java: "bi-filetype-java",
      html: "bi-filetype-html", css: "bi-filetype-css", json: "bi-filetype-json",
      md: "bi-markdown", rb: "bi-filetype-rb", go: "bi-filetype-go",
      rs: "bi-filetype-rs", php: "bi-filetype-php",
    };
    return iconMap[ext] || "bi-file-earmark";
  }

  componentWillUnmount() {
    if (this._markerSubscription) {
      this._markerSubscription.dispose();
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  render() {
    const { files, activeFile, showFileTree, followMode, followTargetName, diagnostics } = this.state;
    const fileList = Object.keys(files);
    const errorCount = diagnostics.filter(d => d.severity === 1).length;
    const warningCount = diagnostics.filter(d => d.severity === 2).length;

    return (
      <div style={{ display: "flex", height: "100vh", width: "90vw" }}>
        {showFileTree && (
          <div className="file-tree-panel">
            <div className="file-tree-header">
              <span className="file-tree-title">Files</span>
              <button className="file-tree-btn" onClick={() => this.createNewFile()} title="New file">
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>
            <div className="file-tree-list">
              {fileList.map((f) => (
                <div
                  key={f}
                  className={`file-tree-item${f === activeFile ? " active" : ""}`}
                  onClick={() => this.switchFile(f)}
                >
                  <i className={`bi ${this.getFileIcon(f)}`}></i>
                  <span className="file-tree-name">{f}</span>
                  {fileList.length > 1 && (
                    <button
                      className="file-tree-delete"
                      onClick={(e) => this.deleteFile(f, e)}
                      title="Delete file"
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="file-tree-footer">
              <button
                className="file-tree-toggle-btn"
                onClick={() => this.setState({ showFileTree: false })}
                title="Hide file tree"
              >
                <i className="bi bi-panel-left"></i>
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div className="editor-toolbar">
            {!showFileTree && (
              <button
                className="toolbar-btn"
                onClick={() => this.setState({ showFileTree: true })}
                title="Show file tree"
              >
                <i className="bi bi-panel-left"></i>
              </button>
            )}
            <div className="toolbar-filename">
              <i className={`bi ${this.getFileIcon(activeFile)}`}></i>
              {activeFile}
            </div>
            <div className="toolbar-spacer"></div>
            {followMode && (
              <div className="toolbar-follow-badge">
                <i className="bi bi-eye"></i>
                Following: {followTargetName}
                <button className="toolbar-follow-stop" onClick={() => this.toggleFollowMode(this.followTarget)}>
                  <i className="bi bi-x"></i>
                </button>
              </div>
            )}
            {(errorCount > 0 || warningCount > 0) && (
              <div className="toolbar-diagnostics">
                {errorCount > 0 && (
                  <span className="diagnostic-count diagnostic-error">
                    <i className="bi bi-x-circle-fill"></i> {errorCount}
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="diagnostic-count diagnostic-warning">
                    <i className="bi bi-exclamation-triangle-fill"></i> {warningCount}
                  </span>
                )}
              </div>
            )}
            <div className="toolbar-follow-section">
              <span className="toolbar-label">Follow:</span>
              {(this.props.loggedinUsers || []).filter(u => u.userId !== this.state.clientId).map((u) => (
                <button
                  key={u.userId}
                  className={`toolbar-user-chip${followMode && this.followTarget === u.userId ? " active" : ""}`}
                  style={{ borderColor: u.color }}
                  onClick={() => this.toggleFollowMode(u.userId)}
                  title={`Follow ${u.name}`}
                >
                  <span className="user-chip-dot" style={{ background: u.color }}></span>
                  {u.name}
                </button>
              ))}
            </div>
          </div>

          <Widget
            handleNewUserMessage={this.handleNewUserMessage}
            title="Interview Pad"
            subtitle="Developed by Darshan Marathe"
          />

          <Editor
            height="100%"
            width="100%"
            theme={"vs-" + this.props.theme}
            style={{ float: "left" }}
            path={this.state.activeFile + "-" + this.state.language}
            defaultLanguage={this.state.language}
            value={this.state.code}
            onMount={this.handleEditorDidMount}
          />
        </div>
      </div>
    );
  }
}
